import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getSocialPostEngagement } from "../api/AIContentApi";
import { getAppLink } from "../api/AppLinkApi";
import {
  Calendar,
  ChevronDownIcon,
  Sparkles,
  Camera,
  Video,
  PenTool,
  PenLine,
  Trash2,
  Copy,
  DownloadCloud,
  RotateCcw,
  PlayCircle,
  SquarePen,
  Ban,
  CalendarCheck,
  CalendarClock,
  PencilLine,
  Info,
  ArrowUp,
  X,
  Brush,
} from "lucide-react";
import { Tooltip } from "react-tooltip";
import AIContentAction from "../actions/AIContentAction";
import { fromJS } from "immutable";
import { format, isValid, isWeekend, parse } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { Modal } from "../components/ui";
import SelectField from "../components/ui/SelectField";
import { DateTimePickerField } from "../components/ui";
import { deleteTask, downloadFileLogActivity, searchFiles, updateItem } from "../api/ProjectApi";
import LayoutAction from "../actions/LayoutAction";
import ConfirmDialog from "../dialog/ConfirmDialog";
import { getBaseUrl } from "../api/api";
import {
  generateContents,
  scheduleSocialPost,
  cancelSocialPost,
  retrySocialPost,
  publishNowSocialPost,
  getSocialPostStatus,
} from "../api/AIContentApi";
import { contentListToContentByDate } from "./types/AIContentTypes";
import { getDesign } from "../api/CanvaApi";
import ChartLoading from "../insight/components/ChartLoading";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import BillingAction from "../actions/BillingAction";
import { handleAiContentQuotaError } from "./utils/aiContentQuotaUtils";
import { PLATFORM_IMAGE, PLATFORM_POST_LIMITS, PLATFORM_SHORT_TEXT } from "./constants/platform";
import { getStatusStyle } from "./constants/statusConstants";
import { CONTENT_TYPE_OPTIONS } from "./constants/contentTypeConstants";
import { TIMEZONE_OPTIONS } from "./constants/timezoneConstants";
import ReviewPost from "./components/ReviewPost";
import PostActionButton from "./components/PostActionButton";
import PostImage from "./components/PostImage";
import ThreadsInsightsPanel from "./components/review/ThreadsInsightsPanel";
import InstagramInsightsPanel from "./components/review/InstagramInsightsPanel";
import FacebookPageInsightsPanel from "./components/review/FacebookPageInsightsPanel";
import { downloadImage, localDateTimeInTimeZone } from "./helpers";

let CHANGE_TIMEOUT: string | number | NodeJS.Timeout | null | undefined = null;

// Wrapper component to fetch engagement data for Facebook Page Insights Panel
const FacebookInsightsPanelWrapper = ({
  postId,
  projectId,
  pageId,
}: {
  postId: string;
  projectId: string;
  pageId: string;
}) => {
  const { data } = useQuery({
    queryKey: ["social-post-engagement", postId],
    queryFn: async () => {
      const res = await getSocialPostEngagement(projectId, postId);
      return res;
    },
    enabled: !!projectId && !!postId,
  });

  return (
    <FacebookPageInsightsPanel
      pageId={pageId}
      engagement={data?.metrics}
      platformPostId={data?.platformPostId}
    />
  );
};

// Wrapper component to fetch engagement data for Instagram Insights Panel
const InstagramInsightsPanelWrapper = ({
  postId,
  projectId,
}: {
  postId: string;
  projectId: string;
}) => {
  const { data: engagement } = useQuery({
    queryKey: ["social-post-engagement", postId],
    queryFn: async () => {
      const data = await getSocialPostEngagement(projectId, postId);
      return data;
    },
    enabled: !!projectId && !!postId,
  });

  const platformPostId = engagement?.platformPostId || "";

  return (
    <InstagramInsightsPanel engagement={fromJS(engagement || {})} platformPostId={platformPostId} />
  );
};

// Wrapper component to fetch engagement data for Threads Insights Panel
const ThreadsInsightsPanelWrapper = ({
  postId,
  projectId,
}: {
  postId: string;
  projectId: string;
}) => {
  const { data: engagement } = useQuery({
    queryKey: ["social-post-engagement", postId],
    queryFn: async () => {
      const data = await getSocialPostEngagement(projectId, postId);
      return data;
    },
    enabled: !!projectId && !!postId,
  });

  const platformPostId = engagement?.platformPostId || "";

  return (
    <ThreadsInsightsPanel
      engagement={fromJS(engagement || {})}
      postId={postId}
      platformPostId={platformPostId}
    />
  );
};

const PostDetailModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();

  const { data: facebookAppLink } = useQuery({
    queryKey: ["app-link", "facebook"],
    queryFn: async () => {
      const data = await getAppLink("facebook");
      return data;
    },
  });

  const selectedFacebookPageId = facebookAppLink?.data?.page_id || "";

  const PostTypeIcon = {
    image: <Camera className="w-3.5 h-3.5 text-gray-600" />,
    video: <Video className="w-3.5 h-3.5 text-gray-600" />,
    text: <PenTool className="w-3.5 h-3.5 text-gray-600" />,
  };

  const [showConfirmDeleteImage, setShowConfirmDeleteImage] = useState(false);

  const [isEditContent, setIsEditContent] = useState(false);

  const [isLoadingReGenerate, setIsLoadingReGenerate] = useState(false);
  // Custom prompt input state for "Rewrite with AI" feature
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);
  const [customPromptValue, setCustomPromptValue] = useState("");
  const customPromptRef = useRef<HTMLTextAreaElement>(null);
  // True while any async post action is in-flight — locks modal close and all action buttons
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmDeletePost, setShowConfirmDeletePost] = useState(false);
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const [showPreviewPost, setShowPreviewPost] = useState(false);
  const [showFacebookInsights, setShowFacebookInsights] = useState(false);
  const [showThreadsInsights, setShowThreadsInsights] = useState(false);
  const [showInstagramInsights, setShowInstagramInsights] = useState(false);

  const textareaRef = useRef(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const isSysAdmin = useSelector((state: any) =>
    state.userReducer.getIn(["currentUser", "sysAdmin"])
  );
  const showPostDetail = useSelector(
    (state: any) => state.aiContent.get("showPostDetail") || false
  );
  const postDetail = useSelector((state: any) => state.aiContent.get("postDetail")) || fromJS({});
  const aiSetting =
    useSelector((state: any) => state.projectReducer.getIn(["project", "aiSetting"])) || fromJS({});
  const fileImages = useSelector((state: any) => state.aiContent.get("fileImages")) || fromJS([]);
  const canvaDesigns =
    useSelector((state: any) => state.aiContent.get("canvaDesigns")) || fromJS([]);

  const id = postDetail.get("id");
  const start = postDetail.getIn(["data", "start", "value"]);
  const scheduledTime = postDetail.getIn(["data", "scheduledTime", "value"]);
  const timezone = postDetail.getIn(["data", "timezone", "value"]);
  const topic = postDetail.getIn(["data", "topic", "value"]);
  const platform = postDetail.getIn(["data", "platform", "value"]);
  const status = postDetail.getIn(["data", "status", "value"]);
  const postType = postDetail.getIn(["data", "postType", "value"]) || "image";
  const mediaId = postDetail.getIn(["data", "mediaId", "value"]);
  const mediaType = postDetail.getIn(["data", "mediaType", "value"]);

  const isPlatformShortText = PLATFORM_SHORT_TEXT.includes(platform);
  const isEdit = status === "DRAFT";

  const content = isPlatformShortText
    ? postDetail.getIn(["data", "summary", "value"])
    : postDetail.getIn(["data", "description", "value"]);
  const startDate = localDateTimeInTimeZone(start, timezone);
  const scheduledDateTime = localDateTimeInTimeZone(scheduledTime, timezone);

  const { data: hostImageDetail } = useQuery({
    queryKey: ["post-host-image", mediaId],
    queryFn: async () => {
      const data = await searchFiles({
        projectId,
        filters: { fileName: { operator: "in", values: [mediaId] } },
      });
      const images = data?.pageResult?.content || [];
      return images.length > 0 ? images[0] : null;
    },
    enabled: !!mediaId && mediaType === "HOST",
  });

  const { data: canvaImageDetail } = useQuery({
    queryKey: ["post-canva-image", mediaId],
    queryFn: async () => {
      const data = await getDesign(mediaId);
      return data?.design || null;
    },
    enabled: !!mediaId && mediaType === "CANVA",
  });

  const { data: postInfoStatus } = useQuery({
    queryKey: ["social-post-status", projectId, id],
    queryFn: async () => {
      const data = await getSocialPostStatus(projectId, id);
      return data;
    },
    enabled: !!projectId && !!id && (status === "FAILED" || status === "PUBLISHING"),
  });

  useEffect(() => {
    if (projectId) {
      dispatch(AIContentAction.getFileImages(projectId));
      dispatch(AIContentAction.getCanvaDesigns());
    }
  }, [projectId, dispatch]);

  // Auto scroll to top when insights panels are shown
  useEffect(() => {
    if (showFacebookInsights && modalContentRef.current) {
      // Scroll to top of modal content when FacebookPageInsightsPanel is shown
      modalContentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [showFacebookInsights]);

  // Auto scroll to top when Threads insights panel is shown
  useEffect(() => {
    if (showThreadsInsights && modalContentRef.current) {
      // Scroll to top of modal content when ThreadsInsightsPanel is shown
      modalContentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [showThreadsInsights]);

  // Auto scroll to top when Instagram insights panel is shown
  useEffect(() => {
    if (showInstagramInsights && modalContentRef.current) {
      // Scroll to top of modal content when InstagramInsightsPanel is shown
      modalContentRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [showInstagramInsights]);

  const renderStatus = () => {
    const style = getStatusStyle(String(status || "").toUpperCase());
    return (
      <div className={`${style.bgClass} flex items-center gap-2 px-2.5 py-2.5 rounded-full`}>
        <div className={`w-1.5 h-1.5 rounded-full ${style.dotClass}`} />
        <span className={`text-xs font-medium ${style.textClass}`}>{t(style.labelKey)}</span>
        {isSysAdmin && (status === "FAILED" || status === "PUBLISHING") && postInfoStatus?.error ? (
          <span className="text-red-800 cursor-pointer info-anchor-element">
            <Info className="w-4 h-4" />
            <Tooltip anchorSelect=".info-anchor-element" content={postInfoStatus?.error} />
          </span>
        ) : null}
      </div>
    );
  };

  const renderActions = () => {
    if (status === "DRAFT") {
      return (
        <PostActionButton
          text={t("Update")}
          onClick={() => {
            const isValid = validatePost(true);
            if (!isValid) return;
            setShowConfirm({
              show: true,
              title: t("Save"),
              description: t("Are you sure you would like to save all changes for this post?"),
              type: "UPDATE_ALL",
            });
          }}
          icon={<Sparkles className="w-4 h-4" />}
          disabled={isProcessing || isLoadingReGenerate}
          actionButtons={[
            {
              text: t("Cancel this Post"),
              icon: <Ban className="w-3.5 h-3.5" />,
              onClick: () => {
                setShowConfirm({
                  show: true,
                  title: t("Cancel this Post"),
                  description: t("Are you sure you want to cancel this post?"),
                  type: "UPDATE_STATUS_CANCELLED",
                });
              },
            },
            {
              text: t("Approve to Publish"),
              icon: <CalendarClock className="w-3.5 h-3.5" />,
              onClick: () => {
                const isValid = validatePost(true);
                if (!isValid) return;
                setShowConfirm({
                  show: true,
                  title: t("Approve to Publish"),
                  description: t("Are you sure you would like to publish on these schedule date?"),
                  type: "UPDATE_STATUS_SCHEDULED",
                });
              },
            },
            {
              text: t("Publish Now"),
              icon: <CalendarCheck className="w-3.5 h-3.5" />,
              onClick: () => {
                const isValid = validatePost(false);
                if (!isValid) return;
                setShowConfirm({
                  show: true,
                  title: t("Publish Now"),
                  description: t("Are you sure you want to post this now?"),
                  type: "UPDATE_STATUS_PUBLISHING",
                });
              },
            },
          ]}
        />
      );
    } else if (status === "SCHEDULED") {
      return (
        <PostActionButton
          text={t("Unschedule this Post")}
          onClick={() => {
            setShowConfirm({
              show: true,
              title: t("Unschedule this Post"),
              description: t("Are you sure you want to unschedule this post?"),
              type: "UPDATE_STATUS_CANCELLED",
            });
          }}
          icon={<Ban className="w-4 h-4" />}
          disabled={isProcessing}
          actionButtons={[]}
        />
      );
    } else if (status === "PUBLISHING") {
    } else if (status === "PUBLISHED") {
    } else if (status === "FAILED") {
      return (
        <PostActionButton
          text={t("Retry")}
          onClick={() => {
            setShowConfirm({
              show: true,
              title: t("Retry"),
              description: t("Are you sure you want to retry this post?"),
              type: "RETRY_WHEN_FAILED",
            });
          }}
          icon={<RotateCcw className="w-4 h-4" />}
          disabled={isProcessing}
          actionButtons={[
            {
              text: t("Back to Draft"),
              icon: <PencilLine className="w-3.5 h-3.5" />,
              onClick: () => {
                setShowConfirm({
                  show: true,
                  title: t("Back to Draft"),
                  description: t("Are you sure you want to go back to draft?"),
                  type: "UPDATE_STATUS_DRAFT",
                });
              },
            },
            {
              text: t("Publish Now"),
              icon: <CalendarCheck className="w-3.5 h-3.5" />,
              onClick: () => {
                setShowConfirm({
                  show: true,
                  title: t("Publish Now"),
                  description: t("Are you sure you want to post this now?"),
                  type: "UPDATE_STATUS_PUBLISHING",
                });
              },
            },
          ]}
        />
      );
    } else if (status === "CANCELLED") {
      return (
        <PostActionButton
          text={t("Back to Draft")}
          onClick={() => {
            setShowConfirm({
              show: true,
              title: t("Back to Draft"),
              description: t("Are you sure you want to go back to draft?"),
              type: "UPDATE_STATUS_DRAFT",
            });
          }}
          icon={<PencilLine className="w-4 h-4" />}
          disabled={isProcessing}
          actionButtons={[]}
        />
      );
    } else if (status === "RETRYING") {
    }
    return (
      <>
        <button
          className="px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleClose}
          disabled={isProcessing}
        >
          {t("Close")}
        </button>
      </>
    );
  };

  // Accepts an optional overridePrompt; falls back to the project-level customPrompt
  const reGenerateContent = (overridePrompt?: string) => {
    const start_date = format(startDate, "yyyy-MM-dd");
    const promptToUse = overridePrompt ?? aiSetting.get("customPrompt");
    setIsLoadingReGenerate(true);
    setIsProcessing(true);
    generateContents(
      projectId,
      topic,
      promptToUse,
      start_date,
      start_date,
      aiSetting.get("totalDays"),
      aiSetting.get("includeWeekends") || false,
      aiSetting.get("language")
    )
      .then(async (data) => {
        const contentByDate = contentListToContentByDate(data?.result?.contentList);
        const dataByDate = contentByDate[start_date];
        if (dataByDate) {
          const contentData = isPlatformShortText ? dataByDate.summary : dataByDate.description;
          if (textareaRef.current) {
            textareaRef.current.value = contentData || "";
          }
          setIsEditContent(false);
          dispatch(
            AIContentAction.updatePostDetail({
              ...postDetail.toJS(),
              data: {
                ...postDetail.get("data").toJS(),
                description: {
                  value: dataByDate.description,
                  label: "description",
                  type: "HTML",
                },
                summary: {
                  value: dataByDate.summary,
                  label: "summary",
                  type: "string",
                },
              },
            })
          );
          dispatch(
            LayoutAction.setNotification({
              title: t("Generate Content"),
              type: "success",
              message: t("The post content has been updated"),
            })
          );
        }
        if (projectId) {
          dispatch(BillingAction.getBillingTokenUsage(projectId) as any);
        }
      })
      .catch((err) => {
        if (handleAiContentQuotaError(dispatch, err)) {
          if (projectId) {
            dispatch(BillingAction.getBillingTokenUsage(projectId) as any);
          }
          return;
        }
        dispatch(
          LayoutAction.setNotification({
            title: t("Generate Content"),
            type: "error",
            message: t("Failed to generate content"),
          })
        );
      })
      .finally(() => {
        setIsLoadingReGenerate(false);
        setIsProcessing(false);
      });
  };

  // Show the custom prompt input and auto-focus it
  const handleRewriteWithAI = () => {
    if (isLoadingReGenerate || isProcessing) return;
    setShowCustomPrompt(true);
    setTimeout(() => customPromptRef.current?.focus(), 50);
  };

  // Handle keyboard events in the custom prompt textarea
  const handleCustomPromptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const trimmed = customPromptValue.trim();
      if (!trimmed) return;
      setShowCustomPrompt(false);
      setCustomPromptValue("");
      reGenerateContent(trimmed);
    }
    if (e.key === "Escape") {
      setShowCustomPrompt(false);
      setCustomPromptValue("");
    }
  };

  const handleSubmitRemovePost = async () => {
    await deleteTask(id);
    setShowConfirmDeletePost(false);
  };

  const validatePost = (checkScheduleTime) => {
    if (platform === "instagram" && !mediaId) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Platform Instagram"),
          type: "warning",
          message: t("Image is required"),
        })
      );
      return false;
    }
    if (!postType) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Post Type"),
          type: "warning",
          message: t("Post type is required"),
        })
      );
      return false;
    }
    if (checkScheduleTime && scheduledDateTime.getTime() <= new Date().getTime()) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Schedule Time"),
          type: "warning",
          message: t("Schedule time must be greater than current time"),
        })
      );
      return false;
    }
    if (!timezone) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Timezone"),
          type: "warning",
          message: t("Timezone is required"),
        })
      );
      return false;
    }
    if (!content) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Post Content"),
          type: "warning",
          message: t("Post content is required"),
        })
      );
      return false;
    }
    return true;
  };

  const updateBeforePublish = (dateTime) => {
    const updateData: any = {
      start: postDetail.getIn(["data", "start"]).toJS(),
      scheduledTime: postDetail.getIn(["data", "scheduledTime"]).toJS(),
      timezone: postDetail.getIn(["data", "timezone"]).toJS(),
      dayOfWeek: postDetail.getIn(["data", "dayOfWeek"]).toJS(),
      weekend: postDetail.getIn(["data", "weekend"]).toJS(),
      summary: postDetail.getIn(["data", "summary"]).toJS(),
      description: postDetail.getIn(["data", "description"]).toJS(),
      postType: postDetail.getIn(["data", "postType"]).toJS(),
    };
    if (dateTime) {
      const startData = dateTime.getTime();
      // Use shared helper to get the correct local time in the post's timezone
      const dateInTz = localDateTimeInTimeZone(dateTime, timezone);
      const dayOfWeekData = format(dateInTz, "EEEE");
      const weekendData = isWeekend(dateInTz);
      updateData.start = { value: startData, label: "start", type: "datetime" };
      updateData.scheduledTime = { value: startData, label: "scheduledTime", type: "datetime" };
      updateData.dayOfWeek = { value: dayOfWeekData, label: "dayOfWeek", type: "string" };
      updateData.weekend = { value: weekendData, label: "weekend", type: "bool" };
    }
    return updateItem(projectId, id, updateData);
  };

  const updateDefaultImage = (mediaIdData, mediaTypeData, mediaIdsData) => {
    updateItem(projectId, id, {
      mediaId: { value: mediaIdData, label: "mediaId", type: "string" },
      mediaType: { value: mediaTypeData, label: "mediaType", type: "string" },
      mediaIds: {
        value: mediaIdsData.length > 0 ? mediaIdsData.join(",") : "",
        label: "mediaIds",
        type: "stringArray",
      },
    })
      .then(() => {
        dispatch(
          AIContentAction.updatePostDetail({
            ...postDetail.toJS(),
            data: {
              ...postDetail.get("data").toJS(),
              mediaId: { value: mediaIdData, label: "mediaId", type: "string" },
              mediaType: {
                value: mediaTypeData,
                label: "mediaType",
                type: "string",
              },
              mediaIds: {
                value: mediaIdsData,
                label: "mediaIds",
                type: "stringArray",
              },
            },
          })
        );
        dispatch(
          AIContentAction.updateSocialPosts({
            ...postDetail.toJS(),
            data: {
              ...postDetail.get("data").toJS(),
              mediaId: { value: mediaIdData, label: "mediaId", type: "string" },
              mediaType: {
                value: mediaTypeData,
                label: "mediaType",
                type: "string",
              },
              mediaIds: {
                value: mediaIdsData,
                label: "mediaIds",
                type: "stringArray",
              },
            },
          })
        );
      })
      .catch(() => {});
  };

  const handleSubmit = async () => {
    setIsProcessing(true);
    try {
      if (showConfirm.type === "UPDATE_ALL") {
        try {
          await updateItem(projectId, id, {
            start: postDetail.getIn(["data", "start"]).toJS(),
            scheduledTime: postDetail.getIn(["data", "scheduledTime"]).toJS(),
            timezone: postDetail.getIn(["data", "timezone"]).toJS(),
            dayOfWeek: postDetail.getIn(["data", "dayOfWeek"]).toJS(),
            weekend: postDetail.getIn(["data", "weekend"]).toJS(),
            summary: postDetail.getIn(["data", "summary"]).toJS(),
            description: postDetail.getIn(["data", "description"]).toJS(),
            postType: postDetail.getIn(["data", "postType"]).toJS(),
          });
          dispatch(AIContentAction.updateSocialPosts(postDetail.toJS()));
          dispatch(
            LayoutAction.setNotification({
              title: t("Save All"),
              type: "success",
              message: t("Updated post"),
            })
          );
          handleClose();
        } catch {
          dispatch(
            LayoutAction.setNotification({
              title: t("Save All"),
              type: "error",
              message: t("Failed to update post"),
            })
          );
        }
      } else if (showConfirm.type === "UPDATE_STATUS_SCHEDULED") {
        if (status === "DRAFT") {
          await updateBeforePublish(null);
          dispatch(AIContentAction.updateSocialPosts(postDetail.toJS()));
        }
        try {
          const data = await scheduleSocialPost(projectId, id, scheduledDateTime.getTime(), true);
          if (data && data.success) {
            dispatch(
              AIContentAction.updateSocialPosts({
                ...postDetail.toJS(),
                data: {
                  ...postDetail.get("data").toJS(),
                  status: { value: "SCHEDULED", label: "status", type: "string" },
                },
              })
            );
            dispatch(
              LayoutAction.setNotification({
                title: t("Post"),
                type: "success",
                message: t("Scheduled post"),
              })
            );
            handleClose();
          } else {
            dispatch(
              LayoutAction.setNotification({
                title: t("Post"),
                type: "error",
                message: data?.error || t("Failed to schedule post"),
              })
            );
          }
        } catch {
          dispatch(
            LayoutAction.setNotification({
              title: t("Post"),
              type: "error",
              message: t("Failed to schedule post"),
            })
          );
        }
      } else if (showConfirm.type === "UPDATE_STATUS_PUBLISHING") {
        // Save content first — pass null to keep the original date until we confirm publish succeeds
        await updateBeforePublish(null);
        try {
          const data = await publishNowSocialPost(projectId, id);
          if (data && data.success) {
            // Only update date to now after a confirmed successful publish
            const now = new Date();
            const nowTimestamp = now.getTime();
            // Use shared helper to get the correct local time in the post's timezone
            const nowInTz = localDateTimeInTimeZone(now, timezone);
            const nowDayOfWeek = format(nowInTz, "EEEE");
            const nowWeekend = isWeekend(nowInTz);
            await updateItem(projectId, id, {
              start: { value: nowTimestamp, label: "start", type: "datetime" },
              scheduledTime: { value: nowTimestamp, label: "scheduledTime", type: "datetime" },
              dayOfWeek: { value: nowDayOfWeek, label: "dayOfWeek", type: "string" },
              weekend: { value: nowWeekend, label: "weekend", type: "bool" },
            });
            dispatch(
              AIContentAction.updateSocialPosts({
                ...postDetail.toJS(),
                data: {
                  ...postDetail.get("data").toJS(),
                  status: { value: "PUBLISHING", label: "status", type: "string" },
                  start: { value: nowTimestamp, label: "start", type: "datetime" },
                  scheduledTime: { value: nowTimestamp, label: "scheduledTime", type: "datetime" },
                  dayOfWeek: { value: nowDayOfWeek, label: "dayOfWeek", type: "string" },
                  weekend: { value: nowWeekend, label: "weekend", type: "bool" },
                },
              })
            );
            dispatch(
              LayoutAction.setNotification({
                title: t("Post"),
                type: "success",
                message: t("Published post"),
              })
            );
            handleClose();
          } else {
            // success: false — show the platform error, keep modal open, date unchanged
            dispatch(
              LayoutAction.setNotification({
                title: t("Post"),
                type: "error",
                message: data?.error || t("Failed to publish post"),
              })
            );
          }
        } catch {
          dispatch(
            LayoutAction.setNotification({
              title: t("Post"),
              type: "error",
              message: t("Failed to publish post"),
            })
          );
        }
      } else if (showConfirm.type === "UPDATE_STATUS_DRAFT") {
        try {
          await updateItem(projectId, id, {
            status: { value: "DRAFT", label: "status", type: "string" },
          });
          dispatch(
            AIContentAction.updateSocialPosts({
              ...postDetail.toJS(),
              data: {
                ...postDetail.get("data").toJS(),
                status: { value: "DRAFT", label: "status", type: "string" },
              },
            })
          );
          dispatch(
            LayoutAction.setNotification({
              title: t("Post"),
              type: "success",
              message: t("Back to draft post successfully"),
            })
          );
          handleClose();
        } catch {
          dispatch(
            LayoutAction.setNotification({
              title: t("Post"),
              type: "error",
              message: t("Failed to back to draft post"),
            })
          );
        }
      } else if (showConfirm.type === "UPDATE_STATUS_CANCELLED") {
        try {
          const data = await cancelSocialPost(projectId, id);
          if (data && data.success) {
            dispatch(
              AIContentAction.updateSocialPosts({
                ...postDetail.toJS(),
                data: {
                  ...postDetail.get("data").toJS(),
                  status: { value: "CANCELLED", label: "status", type: "string" },
                },
              })
            );
            dispatch(
              LayoutAction.setNotification({
                title: t("Post"),
                type: "success",
                message: t("Cancelled post"),
              })
            );
            handleClose();
          } else {
            dispatch(
              LayoutAction.setNotification({
                title: t("Post"),
                type: "error",
                message: data?.error || t("Failed to cancel post"),
              })
            );
          }
        } catch {
          dispatch(
            LayoutAction.setNotification({
              title: t("Post"),
              type: "error",
              message: t("Failed to cancel post"),
            })
          );
        }
      } else if (showConfirm.type === "RETRY_WHEN_FAILED") {
        try {
          const data = await retrySocialPost(projectId, id);
          if (data && data.success) {
            dispatch(
              AIContentAction.updateSocialPosts({
                ...postDetail.toJS(),
                data: {
                  ...postDetail.get("data").toJS(),
                  status: { value: "RETRYING", label: "status", type: "string" },
                },
              })
            );
            dispatch(
              LayoutAction.setNotification({
                title: t("Post"),
                type: "success",
                message: t("Retrying post"),
              })
            );
            handleClose();
          } else {
            dispatch(
              LayoutAction.setNotification({
                title: t("Post"),
                type: "error",
                message: data?.error || t("Failed to retry post"),
              })
            );
          }
        } catch {
          dispatch(
            LayoutAction.setNotification({
              title: t("Post"),
              type: "error",
              message: t("Failed to retry post"),
            })
          );
        }
      }
    } finally {
      setIsProcessing(false);
      setShowConfirm({ show: false, title: "", description: "" });
    }
  };


  const handleChangePostType = (selectedOption) => {
    if (selectedOption?.value) {
      if (selectedOption.value === "video") {
        dispatch(
          LayoutAction.setNotification({
            title: t("Post Type"),
            type: "warning",
            message: t("Single Video is not currently supported"),
          })
        );
        return;
      }
      dispatch(
        AIContentAction.updatePostDetail({
          ...postDetail.toJS(),
          data: {
            ...postDetail.get("data").toJS(),
            postType: {
              value: selectedOption.value,
              label: "postType",
              type: "string",
            },
          },
        })
      );
    }
  };

  const handleChangeScheduleTime = (value) => {
    if (!value) return;
    const newDate = parse(value, "hh:mm aa, yyyy-MM-dd", new Date());
    const newDateData = zonedTimeToUtc(isValid(newDate) ? newDate : new Date(), timezone);
    const startData = newDateData.getTime();
    const scheduledTimeData = newDateData.getTime();
    const dayOfWeekData = format(newDateData, "EEEE");
    const weekendData = isWeekend(newDateData);
    dispatch(
      AIContentAction.updatePostDetail({
        ...postDetail.toJS(),
        data: {
          ...postDetail.get("data").toJS(),
          start: { value: startData, label: "start", type: "datetime" },
          scheduledTime: {
            value: scheduledTimeData,
            label: "scheduledTime",
            type: "datetime",
          },
          dayOfWeek: {
            value: dayOfWeekData,
            label: "dayOfWeek",
            type: "string",
          },
          weekend: { value: weekendData, label: "weekend", type: "bool" },
        },
      })
    );
  };

  const handleChangeTimezone = (value) => {
    if (!value) return;
    dispatch(
      AIContentAction.updatePostDetail({
        ...postDetail.toJS(),
        data: {
          ...postDetail.get("data").toJS(),
          timezone: { value: value, label: "timezone", type: "string" },
        },
      })
    );
  };

  const handleSaveContent = (content) => {
    if (CHANGE_TIMEOUT) {
      clearTimeout(CHANGE_TIMEOUT);
    }
    CHANGE_TIMEOUT = setTimeout(async () => {
      dispatch(
        AIContentAction.updatePostDetail({
          ...postDetail.toJS(),
          data: {
            ...postDetail.get("data").toJS(),
            summary: { value: content, label: "summary", type: "string" },
            description: {
              value: content,
              label: "description",
              type: "HTML",
            },
          },
        })
      );
    }, 1000);
  };

  const handleRemoveImage = () => {
    if (!mediaId || !mediaType) return;
    setShowConfirmDeleteImage(true);
  };

  const handleSubmitRemoveImage = () => {
    if (!mediaId || !mediaType) return;
    let mediaIdData = "";
    if (mediaType === "CANVA") {
      if (canvaDesigns.size > 0) {
        const canvaDesign = canvaDesigns.first() || fromJS({});
        mediaIdData = canvaDesign.get("id") || "";
      }
    } else {
      if (fileImages.size > 0) {
        const fileImage = fileImages.first() || fromJS({});
        mediaIdData = fileImage.getIn(["event", "data", "fileName"]) || "";
      }
    }
    updateDefaultImage(mediaIdData, mediaType, mediaIdData ? [mediaIdData] : []);
    setShowConfirmDeleteImage(false);
  };

  const handleDuplicateImage = () => {
    if (!mediaId || !mediaType) return;
  };

  const handleDownloadImage = () => {
    if (!mediaId || !mediaType) return;
    if (mediaType === "HOST") {
      downloadFileLogActivity(mediaId).then((url) => {
        downloadImage(url, mediaId);
      });
    } else if (mediaType === "CANVA") {
      if (canvaImageDetail && canvaImageDetail.thumbnail?.url) {
        const url = new URL(canvaImageDetail.thumbnail.url);
        const path = url.pathname;
        const fileName = path.substring(path.lastIndexOf("/") + 1);
        downloadImage(canvaImageDetail.thumbnail.url, fileName);
      }
    }
  };

  const resetModal = () => {
    setIsEditContent(false);
    setShowPreviewPost(false);
    setShowFacebookInsights(false);
    setShowThreadsInsights(false);
    setShowInstagramInsights(false);
  };

  const handleClose = () => {
    // Block closing the modal while an async action is in-flight
    if (isProcessing) return;
    dispatch(AIContentAction.hidePostDetailDialog());
    resetModal();
  };

  const modalContent = (
    <div ref={modalContentRef} className="p-5 space-y-4">
      {/* Top Bar */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 pr-2">
          <img src={`${getBaseUrl()}${PLATFORM_IMAGE[platform]}`} className="h-8" alt={platform} />
          <span className="text-lg font-medium text-gray-800 capitalize">{platform}</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Media Type Dropdown */}
          <div>
            <SelectField
              className="w-max mr-2"
              value={CONTENT_TYPE_OPTIONS.find((option) => option.value === postType)}
              onChange={handleChangePostType}
              options={CONTENT_TYPE_OPTIONS}
              isDisabled={!isEdit}
              dropdownIndicator={<ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" />}
              formatOptionLabel={(option, { context }) => {
                if (context === "value") {
                  return (
                    <div className={"w-full hover:text-blue-600 flex items-center space-x-4"}>
                      <div>{PostTypeIcon[option.value]}</div>
                      <div className="w-px h-5 bg-gray-300" />
                    </div>
                  );
                }
                return (
                  <div
                    className={"w-full hover:text-blue-600 flex items-center space-x-2"}
                    key={option.value}
                  >
                    <div>{PostTypeIcon[option.value]}</div>
                    <div>{t(option.label)}</div>
                  </div>
                );
              }}
              stylesOverride={{
                menu: (base) => ({
                  ...base,
                  width: "max-content",
                  minWidth: "100%",
                }),
              }}
            />
          </div>
          {/* Publish Date Input */}
          <div>
            <DateTimePickerField
              value={format(scheduledDateTime, "hh:mm aa, yyyy-MM-dd")}
              onChange={handleChangeScheduleTime}
              disabledSelectDate={{ before: new Date() }}
              disabled={!isEdit}
              rightAdornment={
                <div className="bg-gray-50 border border-gray-200 rounded-l-lg flex items-center gap-2 px-3 py-2 truncate">
                  <Calendar className="w-3.5 h-3.5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-600 truncate">
                    {t("Publish on")}
                  </span>
                </div>
              }
            />
          </div>
          {/* Timezone Input */}
          <div>
            <SelectField
              value={TIMEZONE_OPTIONS.find((opt) => opt.value === timezone)}
              onChange={(option: any) => {
                handleChangeTimezone(option?.value);
              }}
              options={TIMEZONE_OPTIONS}
              dropdownIndicator={<ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" />}
              // isDisabled={!isEdit}
              isDisabled={true} // Not allow update timezone
              placeholder={t("Select timezone")}
            />
          </div>
          {/* Status Badge */}
          {renderStatus()}
        </div>
      </div>

      {/* Content Area */}
      {showPreviewPost || status === "PUBLISHED" ? (
        <div
          className={`grid ${
            (platform === "facebook" && status === "PUBLISHED" && showFacebookInsights) ||
            (platform === "threads" && status === "PUBLISHED" && showThreadsInsights) ||
            (platform === "instagram" && status === "PUBLISHED" && showInstagramInsights)
              ? "grid-cols-1 lg:grid-cols-2"
              : "grid-cols-1"
          } gap-12 justify-items-center`}
        >
          <div className="w-[456px]">
            <ReviewPost
              postDetail={postDetail}
              onToggleFacebookInsights={() => setShowFacebookInsights(!showFacebookInsights)}
              onToggleThreadsInsights={() => setShowThreadsInsights(!showThreadsInsights)}
              onToggleInstagramInsights={() => setShowInstagramInsights(!showInstagramInsights)}
            />
            {/* Ensure preview components take the same width */}
            <style>{`.w-\\[456px\\] .max-w-xl, .w-\\[456px\\] .max-w-md { max-width: 100%; }`}</style>
          </div>

          {/* Facebook Page Insights Panel - Display on the right side */}
          {platform === "facebook" && status === "PUBLISHED" && showFacebookInsights && (
            <div className="w-[456px]">
              <FacebookInsightsPanelWrapper
                postId={id}
                projectId={projectId}
                pageId={selectedFacebookPageId}
              />
            </div>
          )}

          {/* Threads Insights Panel - Display on the right side */}
          {platform === "threads" && status === "PUBLISHED" && showThreadsInsights && (
            <div className="w-[456px]">
              <ThreadsInsightsPanelWrapper postId={id} projectId={projectId} />
            </div>
          )}

          {/* Instagram Insights Panel - Display on the right side */}
          {platform === "instagram" && status === "PUBLISHED" && showInstagramInsights && (
            <div className="w-[456px]">
              <InstagramInsightsPanelWrapper postId={id} projectId={projectId} />
            </div>
          )}
        </div>
      ) : (
        <div
          className={`grid ${
            postType !== "text" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"
          } gap-12 justify-items-center`}
        >
          {/* Left - Images */}
          {postType !== "text" ? (
            <div className="w-[500px] flex flex-1 flex-col gap-5">
              <PostImage
                postDetail={postDetail}
                fileImages={fileImages}
                hostImageDetail={hostImageDetail}
                updateDefaultImage={updateDefaultImage}
                onRemoveImage={handleRemoveImage}
              />
            </div>
          ) : null}

          {/* Right - Caption */}
          <div className="w-[500px] bg-gray-50 border border-gray-200 rounded-lg p-3 flex flex-col relative">
            {isLoadingReGenerate && (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-3 flex items-center justify-center rounded-lg">
                <ChartLoading label={t("Generating content…")}></ChartLoading>
              </div>
            )}
            <div className="h-[500px] overflow-y-auto pr-2">
              <div className="text-base leading-6 text-gray-800 space-y-2">
                {id ? (
                  <textarea
                    ref={textareaRef}
                    maxLength={PLATFORM_POST_LIMITS[platform]}
                    readOnly={!isEditContent}
                    defaultValue={content}
                    onChange={(e) => {
                      handleSaveContent(e.target.value || "");
                    }}
                    placeholder={t("Add a content...")}
                    className="w-full h-[490px] outline-none resize-none"
                  />
                ) : null}
              </div>
            </div>

            {/* Custom prompt overlay — iOS search bar style, covers Edit/Rewrite buttons */}
            {showCustomPrompt && (
              <div className="absolute bottom-0 left-0 right-0 z-20 rounded-b-lg bg-white border-t border-gray-100 shadow-lg px-3 pt-3 pb-3">
                {/* Input pill */}
                <div className="flex items-center gap-2 bg-gray-100 rounded-2xl px-3 py-2">
                  <textarea
                    ref={customPromptRef}
                    value={customPromptValue}
                    onChange={(e) => {
                      setCustomPromptValue(e.target.value);
                      // Auto-resize: reset then expand to content height
                      e.target.style.height = "auto";
                      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                    }}
                    onKeyDown={handleCustomPromptKeyDown}
                    placeholder={t("Help me write...")}
                    className="flex-1 text-sm resize-none bg-transparent outline-none placeholder:text-gray-400 text-gray-800 leading-snug overflow-y-hidden"
                    style={{ height: "24px" }}
                    rows={1}
                    disabled={isProcessing || isLoadingReGenerate}
                  />
                  {customPromptValue ? (
                    <button
                      className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                      onClick={() => {
                        setCustomPromptValue("");
                        if (customPromptRef.current) {
                          customPromptRef.current.style.height = "24px";
                        }
                      }}
                      title={t("Clear")}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  ) : null}
                </div>
                {/* Bottom row: Cancel + Send */}
                <div className="flex items-center justify-between mt-2">
                  <button
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors px-1"
                    onClick={() => { setShowCustomPrompt(false); setCustomPromptValue(""); }}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      customPromptValue.trim() && !isProcessing && !isLoadingReGenerate
                        ? "bg-[#2563eb] hover:bg-blue-700 cursor-pointer shadow-sm"
                        : "bg-gray-200 cursor-not-allowed"
                    }`}
                    onClick={() => {
                      if (customPromptValue.trim() && !isProcessing && !isLoadingReGenerate) {
                        handleCustomPromptKeyDown({ key: "Enter", shiftKey: false, preventDefault: () => {} } as any);
                      }
                    }}
                    disabled={!customPromptValue.trim() || isProcessing || isLoadingReGenerate}
                    title={t("Generate")}
                  >
                    <ArrowUp className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {isEdit ? (
              <div className="flex items-center justify-between mt-6 pt-3 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  {isEditContent ? (
                    <></>
                  ) : (
                    <div
                      className="bg-gray-500 flex items-center gap-1 px-1.5 py-0.5 rounded-md cursor-pointer"
                      onClick={() => {
                        setIsEditContent(true);
                        textareaRef.current?.focus();
                      }}
                    >
                      <PenLine className="w-2.5 h-2.5 text-white" />
                      <span className="text-xs font-medium text-white">{t("Edit")}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`bg-yellow-500 flex items-center gap-1 px-1.5 py-0.5 rounded-md ${
                      isLoadingReGenerate || isProcessing
                        ? "opacity-60 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    onClick={handleRewriteWithAI}
                  >
                    <Sparkles className="w-2.5 h-2.5 text-white" />
                    <span className="text-xs font-medium text-white">
                      {t("Rewrite with AI")}
                    </span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );

  const modalFooter = (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {postType === "image" ? (
          <>
            <button
              className={`w-[38px] h-[38px] rounded-lg bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:bg-gray-200 ${
                isEdit && mediaType === "HOST" ? "cursor-pointer" : ""
              }`}
              onClick={() => handleRemoveImage()}
              disabled={!isEdit}
              title={t("Delete Image")}
            >
              <Trash2 className="w-4 h-4 text-gray-600" />
            </button>
            <button
              className={`w-[38px] h-[38px] rounded-lg bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:bg-gray-200`}
              onClick={() => handleDuplicateImage()}
              disabled={true}
              title={t("Duplicate Image")}
            >
              <Copy className="w-4 h-4 text-gray-600" />
            </button>
            <button
              className={`w-[38px] h-[38px] rounded-lg bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 disabled:bg-gray-200 cursor-pointer`}
              onClick={() => handleDownloadImage()}
              title={t("Download Image")}
            >
              <DownloadCloud className="w-4 h-4 text-gray-600" />
            </button>
          </>
        ) : null}
        {status !== "PUBLISHED" ? (
          <>
            {showPreviewPost ? (
              <button
                className={`px-3 gap-2 h-[38px] rounded-lg bg-blue-200 text-blue-800 font-semibold border border-gray-200 flex items-center justify-center cursor-pointer`}
                onClick={() => setShowPreviewPost(false)}
              >
                <SquarePen className="w-4 h-4 text-blue-800" />
                {t("Edit")}
              </button>
            ) : (
              <>
                <button
                  className={`px-3 gap-2 h-[38px] rounded-lg bg-blue-200 text-blue-800 font-semibold border border-gray-200 flex items-center justify-center cursor-pointer`}
                  onClick={() => setShowPreviewPost(true)}
                >
                  <PlayCircle className="w-4 h-4 text-blue-800" />
                  {t("Preview")}
                </button>
                {mediaId && mediaType === "CANVA" && canvaImageDetail?.urls?.edit_url ? (
                  <button
                    className={`px-3 gap-2 h-[38px] rounded-lg bg-blue-200 text-blue-800 font-semibold border border-gray-200 flex items-center justify-center cursor-pointer`}
                    onClick={() => {
                      window.open(canvaImageDetail?.urls?.edit_url, "_blank");
                    }}
                  >
                    <Brush className="w-4 h-4 text-blue-800" />
                    {t("Edit Design")}
                  </button>
                ) : null}
              </>
            )}
          </>
        ) : null}
      </div>
      <div className="flex items-center gap-2">{renderActions()}</div>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={showPostDetail}
        onClose={handleClose}
        title={format(startDate, "EEEE, MMMM dd, yyyy")}
        subtitle={
          <div className="flex items-center justify-center gap-2">
            <div className="bg-yellow-500 flex items-center gap-1 px-1.5 py-0.5 rounded-md">
              <Sparkles className="w-2.5 h-2.5 text-white" />
              <span className="text-xs font-medium text-white">{t("AI Topic")}</span>
            </div>
            <span className="text-base font-medium text-gray-800">{topic}</span>
          </div>
        }
        position="center"
        footer={modalFooter}
        className="w-auto max-w-[1200px] max-h-[98vh]"
        nonClosable={isProcessing}
      >
        {modalContent}
      </Modal>

      <ConfirmDialog
        visible={showConfirmDeleteImage}
        title={t("Delete image")}
        description={t(
          "Are you sure you would like to delete this image, this action is unable to undo?"
        )}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={handleSubmitRemoveImage}
        onClose={() => {
          setShowConfirmDeleteImage(false);
        }}
        dangerBtn={true}
      />

      <ConfirmDialog
        visible={showConfirmDeletePost}
        title={t("Delete post")}
        description={t(
          "Are you sure you would like to delete this post, this action is unable to undo?"
        )}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={handleSubmitRemovePost}
        onClose={() => {
          setShowConfirmDeletePost(false);
        }}
        dangerBtn={true}
      />

      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={<OctagonAlertIcon />}
        onSubmit={handleSubmit}
        submitting={isProcessing}
        onClose={() => {
          if (isProcessing) return;
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </>
  );
};

export default PostDetailModal;
